import { useState, useImperativeHandle, forwardRef, useCallback, useEffect, useRef } from 'react';
import { authService } from '../../services/api';
import { useNotification } from '../Notifications/NotificationContext';
import { useMediaUpload } from '../../hooks/useMediaUpload';
import './MediaManager.css';

const DEFAULT_MAX_MIDIAS = parseInt(process.env.REACT_APP_MAX_MIDIAS || '5', 10);

// ---------------------------------------------------------------------------
// Preview de arquivo já salvo no Telegram
// ---------------------------------------------------------------------------
function TelegramPreview({ fileId, botId, mediaType }) {
  const [src, setSrc] = useState(null);
  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!fileId || !botId) { setStatus('idle'); return; }
    let cancelled = false;
    setStatus('loading');
    setSrc(null);
    setProgress(0);

    // Fallback: quando o servidor não envia Content-Length, anima o progresso
    // de forma suave até ~90% enquanto o download acontece.
    let simulated = 0;
    const ticker = setInterval(() => {
      if (cancelled) return;
      simulated = Math.min(90, simulated + Math.max(1, (90 - simulated) * 0.12));
      setProgress((prev) => (prev >= simulated ? prev : Math.round(simulated)));
    }, 120);

    authService.uploads.telegram_preview(fileId, botId, {
      onDownloadProgress: (evt) => {
        if (cancelled || !evt.total) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        setProgress((prev) => (pct > prev ? pct : prev));
      },
    })
      .then((response) => {
        if (cancelled) return;
        setProgress(100);
        const ct = response.headers['content-type'] || 'image/jpeg';
        const blob = new Blob([response.data], { type: ct });
        setSrc(URL.createObjectURL(blob));
        setStatus('ok');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => { cancelled = true; clearInterval(ticker); };
  }, [fileId, botId]);

  if (status === 'idle' || status === 'error') return null;
  if (status === 'loading') return (
    <div className="tg-preview-loading">
      <i className="fas fa-spinner fa-spin tg-preview-loading__spinner" />
      <span className="tg-preview-loading__label">Carregando...</span>
      {/* <div className="tg-preview-loading__track">
        <div className="tg-preview-loading__bar" style={{ width: `${progress}%` }} />
      </div> */}
      {/* <span className="tg-preview-loading__pct">{progress}%</span> */}
    </div>
  );

  if (mediaType === 'video') {
    return (
      <video
        src={src}
        controls
        preload="metadata"
        style={{ width: '100%', maxHeight: 200, borderRadius: 6, marginBottom: 8, background: '#000' }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      style={{ width: '100%', maxHeight: 200, borderRadius: 6, objectFit: 'cover', marginBottom: 8 }}
    />
  );
}

// ---------------------------------------------------------------------------
// Ícone por tipo de mídia
// ---------------------------------------------------------------------------
function MediaTypeIcon({ mediaType }) {
  const map = {
    photo: { icon: 'fa-image', color: 'var(--blue)' },
    video: { icon: 'fa-video', color: 'var(--purple)' },
    audio: { icon: 'fa-music', color: 'var(--success)' },
    voice: { icon: 'fa-microphone', color: 'var(--success)' },
  };
  const cfg = map[mediaType] || { icon: 'fa-file', color: '#888' };
  return <i className={`fas ${cfg.icon}`} style={{ color: cfg.color }} />;
}

// ---------------------------------------------------------------------------
// MediaManager
// ---------------------------------------------------------------------------
const MediaManager = forwardRef(({
  targetType = 'bot',
  targetId,
  botId,
  existingMedia = {},
  onMediaChange,
  // Multi-media props
  multiMedia = false,
  mediaList = [],       // [{media_type, file_id, thumbnail_file_id, _upload_id?}]
  maxMedia = DEFAULT_MAX_MIDIAS,
  // Send mode toggle (shown inside multi-media when >1 items)
  sendMode = 'agrupado',
  onSendModeChange,
  // Overrides
  slotOverride = null,  // sobrescreve o slot multi-media calculado
  mediaLabel = null,    // sobrescreve o label do componente
  uploadContext = 'generic',
}, ref) => {

  // Slot multi-mídia derivado do targetType (ou sobrescrito)
  const multiSlot = slotOverride || (targetType === 'offer' ? 'offer_multi' : 'welcome_multi');

  /* ─── Single-media state (modo padrão) ─── */
  const [pendingFiles, setPendingFiles] = useState({ photo: null, video: null, audio: null });
  const [optimisticMedia, setOptimisticMedia] = useState({ photo: null, video: null, audio: null });

  /* ─── Multi-media state ─── */
  const [pendingMultiFiles, setPendingMultiFiles] = useState([]); // [{tempId, file, mediaType}]
  const multiFileInputRef = useRef(null);

  // IDs de uploads ainda aguardando file_id do Telegram (para polling)
  const [pendingPolls, setPendingPolls] = useState([]); // [{uploadId}]
  const pollIntervalsRef = useRef({});

  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [name, setName] = useState('');

  const { showError, showSuccess } = useNotification();
  const { uploadAndAttach } = useMediaUpload();

  useEffect(() => {
    if (mediaLabel) { setName(mediaLabel); return; }
    if (targetType === 'flow_node') setName('do Nó');
  }, [targetType, mediaLabel]);

  /* ─── config para modo padrão ─── */
  const MEDIA_CONFIG = {
    bot: [
      { type: 'photo', label: 'Imagem', slot: 'welcome' },
      { type: 'video', label: 'Vídeo', slot: 'welcome' },
      { type: 'audio', label: 'Áudio', slot: 'welcome_audio' },
    ],
    offer: [
      { type: 'photo', label: 'Imagem', slot: 'main' },
      { type: 'video', label: 'Vídeo', slot: 'main' },
      { type: 'audio', label: 'Áudio', slot: 'main' },
    ],
    flow_node: [
      { type: 'photo', label: 'Imagem', slot: targetId },
      { type: 'video', label: 'Vídeo', slot: targetId },
      { type: 'audio', label: 'Áudio', slot: targetId },
    ]
  };

  const config = MEDIA_CONFIG[targetType] || [];

  useEffect(() => {
    setOptimisticMedia(prev => ({
      photo: existingMedia.photo ? null : prev.photo,
      video: existingMedia.video ? null : prev.video,
      audio: existingMedia.audio ? null : prev.audio,
    }));
  }, [existingMedia.photo, existingMedia.video, existingMedia.audio]);

  /* ─── Polling para file_ids pendentes ─── */
  useEffect(() => {
    if (!pendingPolls.length) return;

    pendingPolls.forEach(({ uploadId }) => {
      if (pollIntervalsRef.current[uploadId]) return; // já está sendo polled

      const intervalId = setInterval(async () => {
        try {
          const { data } = await authService.uploads.get_upload(uploadId);
          if (data.file_id) {
            clearInterval(pollIntervalsRef.current[uploadId]);
            delete pollIntervalsRef.current[uploadId];
            setPendingPolls(prev => prev.filter(p => p.uploadId !== uploadId));
            onMediaChange?.(prev => (prev || []).map(item =>
              item._upload_id === uploadId
                ? { ...item, file_id: data.file_id, thumbnail_file_id: data.thumbnail_file_id || null, _upload_id: undefined }
                : item
            ));
          }
        } catch {
          clearInterval(pollIntervalsRef.current[uploadId]);
          delete pollIntervalsRef.current[uploadId];
          setPendingPolls(prev => prev.filter(p => p.uploadId !== uploadId));
        }
      }, 2500);

      pollIntervalsRef.current[uploadId] = intervalId;

      // Para após 2 minutos para evitar polling infinito
      setTimeout(() => {
        if (pollIntervalsRef.current[uploadId]) {
          clearInterval(pollIntervalsRef.current[uploadId]);
          delete pollIntervalsRef.current[uploadId];
          setPendingPolls(prev => prev.filter(p => p.uploadId !== uploadId));
        }
      }, 120000);
    });

    return () => {
      // Limpeza ao desmontar o componente
      Object.values(pollIntervalsRef.current).forEach(clearInterval);
    };
  }, [pendingPolls, onMediaChange]);

  /* ─────────────────────────────────────────────────────────────────────────
     FILE SELECT — modo padrão
  ───────────────────────────────────────────────────────────────────────── */
  const handleFileSelect = (e, mediaType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX = 30 * 1024 * 1024;
    if (file.size > MAX) {
      showError(`❌ Arquivo muito grande: ${(file.size / 1024 / 1024).toFixed(2)}MB (máximo 30MB)`);
      return;
    }

    const validTypes = {
      photo: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      video: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'],
      audio: ['audio/ogg', 'audio/x-vorbis+ogg', 'application/ogg', 'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/mp4', 'audio/m4a', 'audio/x-m4a'],
    };

    if (!validTypes[mediaType]?.includes(file.type)) {
      showError(`❌ Tipo inválido para ${mediaType}.`);
      return;
    }

    // Imagem e vídeo são mutuamente exclusivos no modo single-media
    if (mediaType === 'photo') {
      setPendingFiles(prev => ({ ...prev, photo: file, video: null }));
    } else if (mediaType === 'video') {
      setPendingFiles(prev => ({ ...prev, video: file, photo: null }));
    } else {
      setPendingFiles(prev => ({ ...prev, [mediaType]: file }));
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
     FILE SELECT — modo multi-mídia
  ───────────────────────────────────────────────────────────────────────── */
  const handleMultiFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentTotal = (mediaList || []).length + pendingMultiFiles.length;
    const available = maxMedia - currentTotal;
    if (available <= 0) {
      showError(`❌ Limite de ${maxMedia} mídias atingido`);
      e.target.value = '';
      return;
    }

    const MAX = 30 * 1024 * 1024;
    const newPending = [];

    for (const file of files) {
      if (newPending.length >= available) {
        showError(`❌ Apenas ${available} mídia(s) restante(s) podem ser adicionadas`);
        break;
      }
      if (file.size > MAX) {
        showError(`❌ ${file.name} muito grande (máximo 30MB)`);
        continue;
      }

      let mediaType = 'photo';
      if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('audio/')) mediaType = 'voice';

      newPending.push({
        tempId: `${Date.now()}_${Math.random()}`,
        file,
        mediaType,
      });
    }

    if (newPending.length) {
      setPendingMultiFiles(prev => [...prev, ...newPending]);
    }

    // reset input para permitir re-selecionar o mesmo arquivo
    e.target.value = '';
  };

  const removePendingMulti = (tempId) => {
    setPendingMultiFiles(prev => prev.filter(p => p.tempId !== tempId));
  };

  /* ─────────────────────────────────────────────────────────────────────────
     UPLOAD ALL
  ───────────────────────────────────────────────────────────────────────── */
  const uploadAll = useCallback(async (overrideTargetId = null) => {
    const finalTargetId = overrideTargetId || targetId;
    const uploadBotId = botId || targetId;

    /* ── Multi-media ── */
    if (multiMedia) {
      if (!pendingMultiFiles.length) return { success: true };

      let anyFailed = false;
      for (const pending of pendingMultiFiles) {
        setUploading(pending.tempId);

        // uploadAndAttach já exibe o erro internamente em caso de falha
        const result = await uploadAndAttach({
          botId: uploadBotId,
          targetType,
          targetId: finalTargetId,
          file: pending.file,
          slot: multiSlot,
          uploadContext,
          mediaType: pending.mediaType,
        });

        if (!result?.success) { anyFailed = true; continue; }

        setPendingMultiFiles(prev => prev.filter(p => p.tempId !== pending.tempId));

        const fileId = result.upload?.file_id || null;
        const uploadId = result.upload?.id || null;

        onMediaChange?.(prev => [
          ...(prev || []),
          {
            media_type: result.upload?.media_type || pending.mediaType,
            file_id: fileId,
            thumbnail_file_id: result.upload?.thumbnail_file_id || null,
            // Marca para polling se ainda não tem file_id
            ...(fileId ? {} : { _upload_id: uploadId }),
          },
        ]);

        // Inicia polling se file_id ainda não disponível
        if (!fileId && uploadId) {
          setPendingPolls(prev => [...prev, { uploadId }]);
        }
      }

      setUploading(null);
      return { success: !anyFailed };
    }

    /* ── Single-media (comportamento original) ── */
    const filesToUpload = Object.entries(pendingFiles).filter(([, file]) => file !== null);
    if (!filesToUpload.length) return { success: true };

    let anyFailed = false;
    try {
      for (const [mediaType, file] of filesToUpload) {
        setUploading(mediaType);

        const mediaConfig = config.find(c => c.type === mediaType);
        if (!mediaConfig) throw new Error(`Configuração não encontrada para ${mediaType}`);

        const uploadResult = await uploadAndAttach({
          botId: uploadBotId,
          targetType,
          targetId: finalTargetId,
          file,
          slot: mediaConfig.slot,
          uploadContext,
          media_type: mediaType,
          onStatusChange: (status) => {
            if (status?.phase === 'media') {
              setOptimisticMedia(prev => ({
                ...prev,
                [mediaType]: { label: status.fileName || file.name, uploadId: status.upload?.id || null },
              }));
            }
          },
        });

        // uploadAndAttach já exibe o erro internamente em caso de falha.
        // NÃO re-lançar aqui: o catch abaixo mostraria a MESMA mensagem de novo,
        // gerando 2 toasts de erro idênticos. Marca a falha e pula este arquivo.
        if (!uploadResult?.success) { anyFailed = true; continue; }

        setPendingFiles(prev => ({ ...prev, [mediaType]: null }));
        setOptimisticMedia(prev => ({
          ...prev,
          [mediaType]: { label: file.name, uploadId: uploadResult.upload?.id || null },
        }));

        showSuccess(`${mediaConfig.label} salvo(a) com sucesso!`);
      }
    } catch (err) {
      showError(err?.response?.data?.error || err.message);
      anyFailed = true;
    } finally {
      setUploading(null);
    }
    return { success: !anyFailed };
  }, [pendingFiles, pendingMultiFiles, multiMedia, targetType, targetId, botId, config, uploadAndAttach, onMediaChange, showSuccess, showError, multiSlot]);

  /* ─────────────────────────────────────────────────────────────────────────
     DELETE — modo padrão
  ───────────────────────────────────────────────────────────────────────── */
  const handleDeleteMedia = async (mediaType) => {
    try {
      setDeleting(mediaType);
      const mediaConfig = config.find(c => c.type === mediaType);
      if (!mediaConfig) throw new Error(`Configuração não encontrada para ${mediaType}`);

      if (targetId) {
        await authService.uploads.detach_media(targetType, targetId, mediaConfig.slot, mediaType);
      }

      onMediaChange?.(prev => ({ ...prev, [mediaType]: null }));
      setPendingFiles(prev => ({ ...prev, [mediaType]: null }));
      setOptimisticMedia(prev => ({ ...prev, [mediaType]: null }));

      showSuccess(`${mediaConfig.label} removido(a) com sucesso!`);
    } catch (err) {
      showError(err?.response?.data?.error || err.message);
    } finally {
      setDeleting(null);
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
     DELETE — modo multi-mídia
  ───────────────────────────────────────────────────────────────────────── */
  const handleDeleteMultiMedia = async (fileId) => {
    try {
      setDeleting(fileId);
      await authService.uploads.detach_media(targetType, targetId, multiSlot, null, fileId);
      onMediaChange?.(prev => (prev || []).filter(item => item.file_id !== fileId));
      showSuccess('Mídia removida com sucesso!');
    } catch (err) {
      showError(err?.response?.data?.error || err.message);
    } finally {
      setDeleting(null);
    }
  };

  useImperativeHandle(ref, () => ({ uploadAll }), [uploadAll]);

  /* ─────────────────────────────────────────────────────────────────────────
     RENDER — modo multi-mídia
  ───────────────────────────────────────────────────────────────────────── */
  if (multiMedia) {
    const totalCount = (mediaList || []).length + pendingMultiFiles.length;
    const atMax = totalCount >= maxMedia;

    return (
      <div className="media-manager">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <label className="spt-label" style={{ marginBottom: 0 }}>
            Mídias {name} <span className='text-xs text-white/40'>(Máx: 30MB cada)</span>
          </label>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {totalCount}/{maxMedia}
          </span>
        </div>

        <div className="media-grid">
          {/* Mídias já salvas */}
          {(mediaList || []).map((item, idx) => {
            const isPending = !item.file_id && item._upload_id;
            return (
              <div key={item.file_id || item._upload_id || idx} className="media-grid-item">
                {isPending ? (
                  <div className="media-grid-audio-icon">
                    <i className="fas fa-spinner fa-spin" style={{ color: 'var(--blue)', fontSize: 28 }} />
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>processando...</span>
                  </div>
                ) : (item.media_type === 'photo' || item.media_type === 'video') ? (
                  <div className="media-grid-preview">
                    <TelegramPreview fileId={item.file_id} botId={botId} mediaType={item.media_type} />
                  </div>
                ) : (
                  <div className="media-grid-audio-icon">
                    <MediaTypeIcon mediaType={item.media_type} />
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {item.file_id ? item.file_id.substring(0, 20) + '...' : 'processando...'}
                    </span>
                  </div>
                )}
                <div className="media-grid-overlay">
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => handleDeleteMultiMedia(item.file_id)}
                    disabled={isPending || !item.file_id || deleting === item.file_id || !!uploading}
                  >
                    <i className="fas fa-trash" />
                    {isPending ? ' Processando...' : deleting === item.file_id ? ' Removendo...' : ' Remover'}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Arquivos pendentes */}
          {pendingMultiFiles.map(pending => (
            <div key={pending.tempId} className="media-grid-item media-grid-item--pending">
              <div className="media-grid-pending-icon">
                {uploading === pending.tempId
                  ? <i className="fas fa-spinner fa-spin" style={{ color: 'var(--success)', fontSize: 32 }} />
                  : <MediaTypeIcon mediaType={pending.mediaType} />
                }
              </div>
              <span className="media-grid-filename">{pending.file.name}</span>
              <span className="media-grid-status">
                {uploading === pending.tempId ? 'enviando...' : 'pendente'}
              </span>
              {uploading !== pending.tempId && (
                <button
                  type="button"
                  className="btn-delete media-grid-remove"
                  onClick={() => removePendingMulti(pending.tempId)}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          ))}

          {/* Botão de adicionar — oculto quando no limite */}
          {!atMax && (
            <label className="media-grid-add">
              <input
                ref={multiFileInputRef}
                type="file"
                accept="image/*,video/*,audio/ogg,audio/mpeg,audio/wav,audio/mp4,audio/m4a,.ogg,.mp3,.wav,.m4a"
                multiple
                onChange={handleMultiFileSelect}
                disabled={!!uploading || !!deleting}
                className="file-input-hidden"
              />
              <i className="fas fa-plus" style={{ fontSize: 28, color: 'var(--purple)' }} />
              <span className="media-grid-add-text">Adicionar mídia</span>
            </label>
          )}
        </div>

        {/* Toggle SEPARADO / AGRUPADO — só aparece quando há mais de 1 mídia */}
        {onSendModeChange && totalCount > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Enviar mídias:</span>
            <div style={{ display: 'flex', border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
              {['agrupado', 'separado'].map(mode => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onSendModeChange(mode)}
                  style={{
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: 'pointer',
                    background: sendMode === mode ? 'var(--purple)' : 'transparent',
                    color: sendMode === mode ? '#fff' : 'var(--text-secondary)',
                    transition: 'all 0.15s',
                  }}
                >
                  {mode === 'agrupado' ? 'Agrupado' : 'Separado'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     RENDER — modo padrão (single-media, comportamento original)
  ───────────────────────────────────────────────────────────────────────── */
  const hasPending = pendingFiles.photo || pendingFiles.video || pendingFiles.audio;
  const hasExisting = existingMedia?.photo || existingMedia?.video || existingMedia?.audio
    || optimisticMedia.photo || optimisticMedia.video || optimisticMedia.audio;

  return (
    <div className="media-manager">
      <div className="media-manager-section w-full">
        <label className="spt-label">Mídia {name} (Máx: 30MB)</label>

        <label className="file-input-label">
          <input
            type="file"
            accept="image/*,video/*,audio/ogg,audio/mpeg,audio/wav,audio/mp4,audio/m4a,.ogg,.mp3,.wav,.m4a"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              let type = 'audio';
              if (file.type.startsWith('image/')) type = 'photo';
              else if (file.type.startsWith('video/')) type = 'video';
              handleFileSelect(e, type);
            }}
            disabled={!!uploading || !!deleting}
            className="file-input-hidden"
          />
          <span className="file-input-icon"><i className="fas fa-cloud-upload-alt" /></span>
          <span className="file-input-text">Clique ou arraste imagem, vídeo ou áudio (.ogg)</span>
        </label>

        {/* Arquivos pendentes */}
        {hasPending && (
          <div className="media-list">
            {pendingFiles.photo && (
              <div className="media-item pending">
                <div className="media-info">
                  <i className="fas fa-hourglass-half" style={{ color: 'var(--success)' }} />
                  <span><strong>{pendingFiles.photo.name}</strong> (pendente)</span>
                </div>
                <button type="button" className="btn-delete" onClick={() => setPendingFiles(p => ({ ...p, photo: null }))}>
                  <i className="fas fa-times" />
                </button>
              </div>
            )}
            {pendingFiles.video && (
              <div className="media-item pending">
                <div className="media-info">
                  <i className="fas fa-hourglass-half" style={{ color: 'var(--success)' }} />
                  <span><strong>{pendingFiles.video.name}</strong> (pendente)</span>
                </div>
                <button type="button" className="btn-delete" onClick={() => setPendingFiles(p => ({ ...p, video: null }))}>
                  <i className="fas fa-times" />
                </button>
              </div>
            )}
            {pendingFiles.audio && (
              <div className="media-item pending">
                <div className="media-info">
                  <i className="fas fa-hourglass-half" style={{ color: 'var(--success)' }} />
                  <span><strong>{pendingFiles.audio.name}</strong> (pendente)</span>
                </div>
                <button type="button" className="btn-delete" onClick={() => setPendingFiles(p => ({ ...p, audio: null }))}>
                  <i className="fas fa-times" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Mídias já salvas / em processamento */}
        {hasExisting && (
          <div className="media-list">
            {existingMedia.photo && (
              <div className="media-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <TelegramPreview fileId={existingMedia.photo} botId={botId} mediaType="photo" />
                <div className="media-info" style={{ justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fas fa-image" style={{ color: 'var(--blue)' }} />
                    Imagem: {existingMedia.photo.substring(0, 30)}...
                  </span>
                  <button type="button" className="btn-delete" onClick={() => handleDeleteMedia('photo')} disabled={deleting === 'photo' || !!uploading}>
                    <i className="fas fa-trash" />
                    {deleting === 'photo' ? 'Removendo...' : 'Remover'}
                  </button>
                </div>
              </div>
            )}
            {!existingMedia.photo && optimisticMedia.photo && (
              <div className="media-item">
                <div className="media-info">
                  <i className="fas fa-image" style={{ color: 'var(--blue)' }} />
                  <span>Mídia: {optimisticMedia.photo.label.substring(0, 40)}...</span>
                </div>
                <button type="button" className="btn-delete" disabled><i className="fas fa-spinner fa-spin" /> Processando...</button>
              </div>
            )}
            {existingMedia.video && (
              <div className="media-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <TelegramPreview fileId={existingMedia.video} botId={botId} mediaType="video" />
                <div className="media-info" style={{ justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fas fa-video" style={{ color: 'var(--purple)' }} />
                    Vídeo: {existingMedia.video.substring(0, 30)}...
                  </span>
                  <button type="button" className="btn-delete" onClick={() => handleDeleteMedia('video')} disabled={deleting === 'video' || !!uploading}>
                    <i className="fas fa-trash" />
                    {deleting === 'video' ? 'Removendo...' : 'Remover'}
                  </button>
                </div>
              </div>
            )}
            {!existingMedia.video && optimisticMedia.video && (
              <div className="media-item">
                <div className="media-info">
                  <i className="fas fa-video" style={{ color: 'var(--purple)' }} />
                  <span>Mídia: {optimisticMedia.video.label.substring(0, 40)}...</span>
                </div>
                <button type="button" className="btn-delete" disabled><i className="fas fa-spinner fa-spin" /> Processando...</button>
              </div>
            )}
            {existingMedia.audio && (
              <div className="media-item">
                <div className="media-info">
                  <i className="fas fa-music" style={{ color: 'var(--purple)' }} />
                  <span>Áudio: {existingMedia.audio.substring(0, 40)}...</span>
                </div>
                <button type="button" className="btn-delete" onClick={() => handleDeleteMedia('audio')} disabled={deleting === 'audio' || !!uploading}>
                  <i className="fas fa-trash" />
                  {deleting === 'audio' ? 'Removendo...' : 'Remover'}
                </button>
              </div>
            )}
            {!existingMedia.audio && optimisticMedia.audio && (
              <div className="media-item">
                <div className="media-info">
                  <i className="fas fa-music" style={{ color: 'var(--purple)' }} />
                  <span>Mídia: {optimisticMedia.audio.label.substring(0, 40)}...</span>
                </div>
                <button type="button" className="btn-delete" disabled><i className="fas fa-spinner fa-spin" /> Processando...</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

MediaManager.displayName = 'MediaManager';
export { MediaManager };
